import {Component, inject, signal} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {RepoService} from "../services/repo.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {AsyncPipe, NgOptimizedImage} from "@angular/common";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {VersionHistory} from "../models/version-history";
import {MatProgressSpinner} from "@angular/material/progress-spinner";

@Component({
  selector: 'app-version-history',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    NgOptimizedImage,
    AsyncPipe,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatProgressSpinner
  ],
  templateUrl: './version-history.component.html',
  styleUrl: './version-history.component.css'
})
export class VersionHistoryComponent {

  loading = signal(false)
  versionNumber: string = "";
  date: string = "";
  public repo = inject(RepoService);
  versionHistory = this.repo.listenToVersionHistory()
  private readonly fb = inject(FormBuilder);
  swedishForm: FormGroup = this.fb.group({
    inputFields: this.fb.array([this.createInputField()])
  }, {validators: [this.atLeastOneNotEmptyValidator()]});
  englishForm: FormGroup = this.fb.group({
    inputFields: this.fb.array([this.createInputField()])
  }, {validators: [this.atLeastOneNotEmptyValidator()]});
  germanForm: FormGroup = this.fb.group({
    inputFields: this.fb.array([this.createInputField()])
  }, {validators: [this.atLeastOneNotEmptyValidator()]});
  norwegianForm: FormGroup = this.fb.group({
    inputFields: this.fb.array([this.createInputField()])
  }, {validators: [this.atLeastOneNotEmptyValidator()]});
  private readonly snackBar = inject(MatSnackBar);

  get swedishInputFields() {
    return this.swedishForm.get('inputFields') as FormArray;
  }

  get englishInputFields() {
    return this.englishForm.get('inputFields') as FormArray;
  }

  get germanInputFields() {
    return this.germanForm.get('inputFields') as FormArray;
  }

  get norwegianInputFields() {
    return this.norwegianForm.get('inputFields') as FormArray;
  }

  atLeastOneNotEmptyValidator() {
    return (formGroup: FormGroup) => {
      const inputFields = formGroup.get('inputFields') as FormArray
      if (inputFields.controls.length === 0) {
        return null;
      }
      const atLeastOneNotEmpty = inputFields.controls.some(control => control.get('newInput')?.value !== '');
      return atLeastOneNotEmpty ? null : {atLeastOneNotEmpty: true};
    };
  }

  createInputField(value: string = ''): FormGroup {
    return this.fb.group({
      newInput: [value]
    });
  }

  addInputField() {
    this.swedishInputFields.push(this.createInputField());
    this.englishInputFields.push(this.createInputField());
    this.germanInputFields.push(this.createInputField());
    this.norwegianInputFields.push(this.createInputField());
  }

  async saveVersion() {

    let versionHistory = {
      "en": this.englishInputFields.controls.map(inputGroup => inputGroup.get('newInput')?.value),
      "sv": this.swedishInputFields.controls.map(inputGroup => inputGroup.get('newInput')?.value),
      "de": this.germanInputFields.controls.map(inputGroup => inputGroup.get('newInput')?.value),
      "no": this.norwegianInputFields.controls.map(inputGroup => inputGroup.get('newInput')?.value),
      "date": this.date,
      "version": this.versionNumber
    }

    console.log(versionHistory);

    await this.repo.saveVersionHistory(this.versionNumber, versionHistory);

    this.snackBar.open("Version saved", "Close")

    this.swedishForm.reset();
    this.englishForm.reset();
    this.germanForm.reset();
    this.norwegianForm.reset();
  }


  deleteVersion(versionHistory: VersionHistory) {
    this.repo.deleteVersionHistory(versionHistory);
  }

  editVersion(versionHistory: VersionHistory) {

    this.versionNumber = versionHistory.version
    this.date = versionHistory.date

    this.swedishInputFields.clear();
    this.englishInputFields.clear();
    this.germanInputFields.clear();
    this.norwegianInputFields.clear();

    versionHistory.en.forEach(value => {
      this.swedishInputFields.push(this.createInputField(value));
    })
    versionHistory.sv.forEach(value => {
      this.englishInputFields.push(this.createInputField(value));
    })
    versionHistory.de.forEach(value => {
      this.germanInputFields.push(this.createInputField(value));
    })
    versionHistory.no.forEach(value => {
      this.norwegianInputFields.push(this.createInputField(value));
    })

  }

  async translate() {

    try {
      this.loading.set(true)

      let messages = [];
      if (this.englishForm.valid) {
        messages.push(...this.englishInputFields.controls.map(inputGroup => inputGroup.get('newInput')?.value))
      } else if (this.swedishForm.valid) {
        messages.push(...this.swedishInputFields.controls.map(inputGroup => inputGroup.get('newInput')?.value))
      } else if (this.norwegianForm.valid) {
        messages.push(...this.norwegianInputFields.controls.map(inputGroup => inputGroup.get('newInput')?.value))
      } else if (this.germanForm.valid) {
        messages.push(...this.germanInputFields.controls.map(inputGroup => inputGroup.get('newInput')?.value))
      } else {
        this.loading.set(false);
        return;
      }

      let translations = await this.repo.translateMessage(messages);

      this.englishInputFields.controls = translations.english.map(value => this.createInputField(value));
      this.swedishInputFields.controls = translations.swedish.map(value => this.createInputField(value));
      this.germanInputFields.controls = translations.german.map(value => this.createInputField(value));
      this.norwegianInputFields.controls = translations.norwegian.map(value => this.createInputField(value));


    } catch (e) {
      console.log(e)
    } finally {
      this.loading.set(false);
    }


  }

  deleteInputField() {
    if (this.swedishInputFields.length > 1) {
      this.swedishInputFields.removeAt(this.swedishInputFields.length - 1);
      this.englishInputFields.removeAt(this.englishInputFields.length - 1);
      this.germanInputFields.removeAt(this.germanInputFields.length - 1);
      this.norwegianInputFields.removeAt(this.norwegianInputFields.length - 1);
    }
  }
}
